import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Trash2, 
  ShieldCheck, 
  ExternalLink,
  BadgeCheck,
  Check,
  ArrowUpRight,
  Zap,
  Save,
  Globe,
  Layers,
  Search,
  RotateCw,
  Eye,
  EyeOff,
  Link as LinkIcon,
  ArrowRight,
  FileJson,
  ChevronDown,
  Palette
} from 'lucide-react';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useProject } from '../context/ProjectContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DocModal from '../components/DocModal';
import ContactModal from '../components/ContactModal';
import Skeleton from '../components/Skeleton';
import cssIcon from '../assets/icons/sidebar/css.svg';
import sassIcon from '../assets/icons/sidebar/sass.svg';
import tailwindIcon from '../assets/icons/sidebar/tailwindcss.svg';
import cssModulesIcon from '../assets/icons/sidebar/css-modules.svg';
import styledComponentsIcon from '../assets/icons/sidebar/styled-components.svg';
import jsIcon from '../assets/icons/sidebar/javascript.svg';
import bootstrapIcon from '../assets/icons/sidebar/bootstrap.svg';
import muiIcon from '../assets/icons/sidebar/mui.svg';


const SettingSection = ({ title, desc, children }) => (
  <section className="space-y-6">
    <div>
      <h2 className="text-2xl font-display font-bold text-slate-100">{title}</h2>
      <p className="text-slate-500 mt-1 max-w-2xl">{desc}</p>
    </div>
    <div className="space-y-4">
      {children}
    </div>
  </section>
);

const SectionSkeleton = () => (
  <div className="glass-card overflow-hidden border-white/5 p-10 bg-white/[0.01]">
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-10">
      <div className="flex items-start gap-6 w-full">
        <Skeleton width="56px" height="56px" borderRadius="1rem" />
        <div className="space-y-3 flex-1">
          <Skeleton width="180px" height="24px" />
          <Skeleton width="80%" height="16px" />
          <div className="flex gap-1.5 pt-3">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <Skeleton key={i} width="16px" height="4px" borderRadius="1rem" />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const EndpointItemSkeleton = () => (
  <div className="p-6 border-b border-white/5">
    <div className="flex flex-col lg:flex-row gap-6 lg:items-center">
      <div className="flex-1 space-y-3">
        <div className="flex items-center gap-3">
          <Skeleton width="48px" height="20px" borderRadius="0.25rem" />
          <Skeleton width="40%" height="20px" borderRadius="0.5rem" />
        </div>
        <Skeleton width="60%" height="14px" borderRadius="0.5rem" />
      </div>
    </div>
  </div>
);

const SettingsPageSkeleton = ({ t }) => (
  <div className="max-w-6xl space-y-16 pb-24">
    <header>
      <div className="flex items-center justify-between">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton width="40px" height="40px" borderRadius="0.75rem" />
            <Skeleton width="300px" height="40px" />
          </div>
          <Skeleton width="400px" height="20px" />
        </div>
      </div>
    </header>

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-8 space-y-12">
        <div className="space-y-6">
          <div className="space-y-1">
            <Skeleton width="200px" height="28px" />
            <Skeleton width="350px" height="16px" />
          </div>
          <SectionSkeleton />
        </div>
        <div className="space-y-6">
          <div className="space-y-1">
            <Skeleton width="220px" height="28px" />
            <Skeleton width="380px" height="16px" />
          </div>
          <div className="space-y-6">
             <SectionSkeleton />
             <SectionSkeleton />
          </div>
        </div>
      </div>
    </div>
  </div>
);


const Settings = () => {
  const { t, lang } = useLanguage();
  const { canAccess, activeProject, activeProjectId, projects, setActiveProjectId, refreshProjects, userRole } = useProject();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [pointsPerDay, setPointsPerDay] = useState(5);
  const [mcpTicketLimit, setMcpTicketLimit] = useState(3);
  const [projectLanguage, setProjectLanguage] = useState('en');
  const [styleHandler, setStyleHandler] = useState('css');
  const [testingFramework, setTestingFramework] = useState('playwright');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeDocType, setActiveDocType] = useState(null);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isFigmaConnected, setIsFigmaConnected] = useState(false);
  const [endpoints, setEndpoints] = useState([]);
  const [endpointsPage, setEndpointsPage] = useState(0);
  const [hasMoreEndpoints, setHasMoreEndpoints] = useState(false);
  const [isFetchingEndpoints, setIsFetchingEndpoints] = useState(false);
  const [totalEndpoints, setTotalEndpoints] = useState(0);
  const [modifiedEndpoints, setModifiedEndpoints] = useState({});
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const [integrationSettings, setIntegrationSettings] = useState({
    jira: { url: '', token: '', projectKey: '', enabled: false },
    azure: { organizationUrl: '', token: '', projectKey: '', enabled: false }
  });
  const [isUpdatingIntegrations, setIsUpdatingIntegrations] = useState(false);
  const [scrumRecommendation, setScrumRecommendation] = useState(null);
  const [isLoadingRecommendation, setIsLoadingRecommendation] = useState(false);
  const [hasRecommendation, setHasRecommendation] = useState(false);
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);

  const [isDirty, setIsDirty] = useState(false);


  const openDoc = (type) => {
    setActiveDocType(type);
    setIsDocModalOpen(true);
  };

  const hasChanges = () => isDirty;

  useEffect(() => {
    const handleBeforeNavigate = (e) => {
      if (isDirty) {
        e.preventDefault();
        setPendingPath(e.detail);
        setShowExitModal(true);
      }
    };

    window.addEventListener('viewgate-navigate', handleBeforeNavigate);
    return () => window.removeEventListener('viewgate-navigate', handleBeforeNavigate);
  }, [isDirty]);

  useEffect(() => {
    if (showExitModal || isDocModalOpen) {
      const mainElement = document.querySelector('main');
      if (mainElement) {
        mainElement.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [showExitModal, isDocModalOpen]);

  useEffect(() => {
    if (projects.length > 0 && activeProjectId) {
      const project = projects.find(p => p._id === activeProjectId);
      if (project) {
        setPointsPerDay(project.settings?.pointsPerDay || 5);
        setMcpTicketLimit(project.settings?.mcpTicketLimit || 3);
        setProjectLanguage(project.settings?.language || 'en');
        setStyleHandler(project.settings?.styleHandler || 'css');
        setTestingFramework(project.settings?.testingFramework || 'playwright');
        setIsFigmaConnected(project.settings?.isFigmaConnected || false);
        setLastSyncedAt(project.settings?.lastSyncedAt || null);


        if (project.settings?.integrations) {
          setIntegrationSettings(project.settings.integrations);
        } else {
          setIntegrationSettings({
            jira: { url: '', token: '', projectKey: '', enabled: false },
            azure: { organizationUrl: '', token: '', projectKey: '', enabled: false }
          });
        }
        setIsDirty(false);
        setIsLoading(false);
        // Clear local modifications when project changes
        setModifiedEndpoints({});
      }
    } else {
      setIsLoading(false);
    }
  }, [projects, activeProjectId]);

  const fetchProjectConfig = async () => {
    if (!activeProjectId) return;
    try {
      const response = await api.get(`/projects/${activeProjectId}/config`);
      const settings = response.data;
      if (settings) {
        setPointsPerDay(settings.pointsPerDay || 5);
        setMcpTicketLimit(settings.mcpTicketLimit || 3);
        setProjectLanguage(settings.language || 'en');
        setStyleHandler(settings.styleHandler || 'css');
        setTestingFramework(settings.testingFramework || 'playwright');
        setIsFigmaConnected(settings.isFigmaConnected || false);
        setLastSyncedAt(settings.lastSyncedAt || null);
        
        const integrations = {
          jira: { 
            url: settings.integrations?.jira?.url || '', 
            token: settings.integrations?.jira?.token || '', 
            projectKey: settings.integrations?.jira?.projectKey || '', 
            enabled: settings.integrations?.jira?.enabled || false 
          },
          azure: { 
            organizationUrl: settings.integrations?.azure?.organizationUrl || '', 
            token: settings.integrations?.azure?.token || '', 
            projectKey: settings.integrations?.azure?.projectKey || '', 
            enabled: settings.integrations?.azure?.enabled || false 
          }
        };
        setIntegrationSettings(integrations);
        setHasRecommendation(!!settings.hasCapacityRecommendation);
        setIsDirty(false);
      }
    } catch (err) {
      // Failed to fetch project config
    }
  };

  const fetchScrumRecommendation = async () => {
    if (!activeProjectId) return;
    setIsLoadingRecommendation(true);
    try {
      const response = await api.get(`/projects/${activeProjectId}/capacity-recommendation`);
      setScrumRecommendation(response.data);
    } catch (err) {
      // Failed to fetch scrum recommendation
      setScrumRecommendation(null);
    } finally {
      setIsLoadingRecommendation(false);
    }
  };

  const fetchEndpoints = async (page = 0, search = '', append = false) => {
    if (!activeProjectId) return;
    setIsFetchingEndpoints(true);
    try {
      const response = await api.get(`/projects/${activeProjectId}/endpoints/paginated`, {
        params: { page, limit: 20, search }
      });
      
      const { endpoints: newEndpoints, total, hasMore } = response.data;
      
      // Merge with local modifications to descriptions
      const mergedEndpoints = newEndpoints.map(ep => {
        const key = `${ep.method.toUpperCase()}:${ep.path}`;
        return {
          ...ep,
          description: modifiedEndpoints[key] ?? ep.description
        };
      });

      setEndpoints(prev => append ? [...prev, ...mergedEndpoints] : mergedEndpoints);
      setTotalEndpoints(total);
      setHasMoreEndpoints(hasMore);
      setEndpointsPage(page);
    } catch (err) {
      // Failed to fetch endpoints
    } finally {
      setIsFetchingEndpoints(false);
    }
  };

  useEffect(() => {
    if (activeProjectId) {
      fetchProjectConfig();
      // Only fetch recommendation if the config flag indicates it exists
      if (hasRecommendation) {
        fetchScrumRecommendation();
      } else {
        setScrumRecommendation(null);
      }
      const timer = setTimeout(() => {
        fetchEndpoints(0, searchQuery, false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, activeProjectId, hasRecommendation]);

  const handleSaveSettings = async () => {
    if (!activeProjectId) return;

    let finalFigmaConnected = isFigmaConnected;
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await api.patch(`/projects/${activeProjectId}`, {
        settings: {
          pointsPerDay: parseInt(pointsPerDay),
          mcpTicketLimit: parseInt(mcpTicketLimit),
          language: projectLanguage,
          styleHandler: styleHandler,
          testingFramework: testingFramework,
          integrations: integrationSettings
        }
      });

      // Prepare descriptions update (for the backend endpoints sync logic)
      // We send the current loaded endpoints but ONLY with their updated descriptions
      const modifiedList = Object.entries(modifiedEndpoints).map(([key, desc]) => {
        const [method, path] = key.split(':');
        return { method, path, description: desc };
      });

      if (modifiedList.length > 0 || isFigmaConnected !== finalFigmaConnected) {
          // Note: If we had a backend endpoint for partial updates, we'd use it here.
          // For now, we still use the config endpoint but the backend should ideally 
          // perform an upsert/merge logic for descriptions.
          await api.patch(`/projects/${activeProjectId}/config`, {
            isFigmaConnected: finalFigmaConnected,
            endpoints: Object.keys(modifiedEndpoints).length > 0 ? Object.entries(modifiedEndpoints).map(([key, description]) => {
                const [method, path] = key.split(':');
                return { method, path, description };
            }) : undefined
          });
      }

      setSaveSuccess(true);
      setIsDirty(false);
      setModifiedEndpoints({}); // Success, clear tracking
      refreshProjects();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      // Failed to save settings
      alert('Error saving settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateEndpointDescription = (method, path, description) => {
    const key = `${method.toUpperCase()}:${path}`;
    setModifiedEndpoints(prev => ({
      ...prev,
      [key]: description
    }));
    
    // Also update UI state
    setEndpoints(prev => prev.map(ep => 
      (ep.method === method && ep.path === path) ? { ...ep, description } : ep
    ));
  };




  const handleSafeNavigate = (path) => {
    if (hasChanges()) {
      setPendingPath(path);
      setShowExitModal(true);
    } else {
      navigate(path);
    }
  };

  const confirmExit = () => {
    setShowExitModal(false);
    if (pendingPath) {
      if (pendingPath.projectId) {
        setActiveProjectId(pendingPath.projectId);
      } else if (pendingPath.path) {
        navigate(pendingPath.path);
      }
    }
  };

  if (isLoading) return <SettingsPageSkeleton t={t} />;

  return (
    <div className="max-w-6xl space-y-16 pb-24">
      {createPortal(
        <AnimatePresence mode='wait'>
          {showExitModal && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowExitModal(false)}
                className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
              />
              <motion.div 
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="relative w-full max-w-md glass-card p-8 border-primary/20 shadow-2xl space-y-6"
              >
                <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-500 mx-auto">
                  <Zap size={32} fill="currentColor" />
                </div>
                <div className="text-center space-y-2">
                  <h3 className="text-2xl font-display font-bold text-slate-100">{t('unsavedChanges')}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {t('unsavedChangesDesc')}
                  </p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button 
                    onClick={confirmExit}
                    className="flex-1 py-4 rounded-2xl bg-white/5 border border-white/10 text-sm font-black uppercase tracking-widest text-slate-400 hover:bg-white/10 hover:text-white transition-all"
                  >
                    {t('discard')}
                  </button>
                  <button 
                    onClick={async () => {
                      await handleSaveSettings();
                      confirmExit();
                    }}
                    disabled={isSaving}
                    className="flex-1 py-4 rounded-2xl bg-primary text-white text-sm font-black uppercase tracking-widest shadow-lg shadow-primary/20 hover:bg-blue-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isSaving ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      t('save')
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
      <header>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-display font-black text-slate-100 flex items-center gap-3">
               <BadgeCheck className="text-primary" size={32} /> {t('developerSettings')}
            </h1>
            <p className="text-slate-500 mt-2 text-lg">{t('settingsDesc')}</p>
          </div>
        </div>
      </header>



      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-12">
          
          {/* Sprints Configuration Section */}
          {userRole === 'pm' && (
            <SettingSection 
              title={t('sprintSettings')} 
              desc={t('capacityDesc')}
            >
              {isLoading ? <SectionSkeleton /> : (
                <div className="glass-card overflow-hidden border-white/5 group hover:border-primary/30 transition-all duration-500">
                  <div className="p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
                    <div className="flex items-start gap-6">
                      <div className="relative">
                        <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-500 border border-amber-500/20 shadow-xl shadow-amber-500/5 relative z-10">
                          <Zap size={28} fill="currentColor" />
                        </div>
                        <div className="absolute inset-0 bg-amber-500/20 blur-2xl rounded-full opacity-0 group-hover:opacity-50 transition-opacity" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-xl font-display font-bold text-slate-100">{t('defaultCapacity')}</h4>
                        <p className="text-slate-500 text-sm leading-relaxed max-w-md">
                          {t('capacityDesc')}
                        </p>
                        <div className="flex gap-1.5 pt-3">
                          {[...Array(10)].map((_, i) => (
                            <div 
                              key={i} 
                              className={`h-1 rounded-full transition-all duration-700 ${i < Math.min(pointsPerDay / 2, 10) ? 'w-4 bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]' : 'w-2 bg-white/5'}`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 min-w-[160px]">
                      <div className="relative">
                        <input 
                          type="number" 
                          min="1"
                          max="100"
                          value={pointsPerDay}
                          onChange={(e) => { setPointsPerDay(e.target.value); setIsDirty(true); }}
                          className="w-full bg-slate-950/50 border border-white/10 rounded-2xl pl-6 pr-12 py-3.5 text-2xl text-white font-black focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all outline-none"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black uppercase tracking-widest text-slate-500 pointer-events-none">Pts</span>
                      </div>

                      {/* Scrum Expert Suggestion Card */}
                      <AnimatePresence>
                        {scrumRecommendation && scrumRecommendation.value && scrumRecommendation.reasoning?.[lang] && (
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-5 rounded-2xl bg-slate-900/50 border border-primary/20 space-y-4 relative overflow-hidden group/rec mt-4"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/80">
                                  {t('scrum_expert')}
                                </span>
                                {scrumRecommendation.isLive && (
                                  <span className="px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-500 text-[8px] font-black uppercase tracking-widest border border-emerald-500/20">
                                    {t('live') || 'Live'}
                                  </span>
                                )}
                              </div>
                              <div className="h-px flex-1 bg-gradient-to-r from-primary/20 to-transparent ml-4" />
                            </div>
                            
                            <p className="text-[13px] text-slate-300 leading-relaxed font-medium">
                               "{scrumRecommendation.reasoning?.[lang] || scrumRecommendation.reasoning?.['en']}"
                            </p>
                            
                            <button 
                              onClick={() => { setPointsPerDay(scrumRecommendation.value); setIsDirty(true); }}
                              className="w-full py-3 rounded-xl bg-primary/10 border border-primary/20 text-[10px] font-black uppercase tracking-widest text-primary hover:bg-primary hover:text-white transition-all duration-300"
                            >
                              {t('apply_recommendation')} — {scrumRecommendation.value} pts
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              )}
            </SettingSection>
          )}

          {/* AI Agent Configuration Section */}
          {userRole === 'pm' && (
            <SettingSection 
              title={t('aiAgentSettings')} 
              desc={t('aiAgentSettingsDesc')}
            >
              {isLoading ? <SectionSkeleton /> : (
                <div className="space-y-6">
                  {/* Capacity Card */}
                  <div className="glass-card overflow-hidden border-white/5 group hover:border-primary/30 transition-all duration-500">
                    <div className="p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
                      <div className="flex items-start gap-6">
                        <div className="relative">
                          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary border border-primary/20 shadow-xl shadow-primary/5 relative z-10">
                            <ShieldCheck size={28} />
                          </div>
                          <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-full opacity-0 group-hover:opacity-50 transition-opacity" />
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-xl font-display font-bold text-slate-100">{t('sprintLimit')}</h4>
                          <p className="text-slate-500 text-sm leading-relaxed max-w-md">
                            {t('sprintLimitDesc')}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col gap-3 min-w-[160px]">
                        <div className="relative">
                          <input 
                            type="number" 
                            min="1"
                            max="50"
                            value={mcpTicketLimit}
                            onChange={(e) => { setMcpTicketLimit(e.target.value); setIsDirty(true); }}
                            className="w-full bg-slate-950/50 border border-white/10 rounded-2xl pl-6 pr-12 py-3.5 text-2xl text-white font-black focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all outline-none"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black uppercase tracking-widest text-slate-500 pointer-events-none">Batch</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Language Card */}
                  <div className="glass-card overflow-hidden border-white/5 group hover:border-primary/30 transition-all duration-500">
                    <div className="p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
                      <div className="flex items-start gap-6">
                        <div className="relative">
                          <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 border border-indigo-500/20 shadow-xl shadow-indigo-500/5 relative z-10">
                            <Globe size={28} />
                          </div>
                          <div className="absolute inset-0 bg-indigo-500/20 blur-2xl rounded-full opacity-0 group-hover:opacity-50 transition-opacity" />
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-xl font-display font-bold text-slate-100">{t('aiLanguage')}</h4>
                          <p className="text-slate-500 text-sm leading-relaxed max-w-md">
                            {t('aiLanguageDesc')}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col gap-4 min-w-[200px]">
                        <div className="flex bg-slate-950/50 p-1.5 rounded-2xl border border-white/10 relative z-0">
                          <button 
                            onClick={() => { setProjectLanguage('en'); setIsDirty(true); }}
                            className={`flex-1 py-3 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all relative z-10 ${projectLanguage === 'en' ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}
                          >
                            {projectLanguage === 'en' && (
                              <motion.div 
                                layoutId="lang-bg"
                                className="absolute inset-0 bg-primary rounded-xl -z-10 shadow-lg shadow-primary/25"
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                              />
                            )}
                            {t('english')}
                          </button>
                          <button 
                            onClick={() => { setProjectLanguage('es'); setIsDirty(true); }}
                            className={`flex-1 py-3 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all relative z-10 ${projectLanguage === 'es' ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}
                          >
                            {projectLanguage === 'es' && (
                              <motion.div 
                                layoutId="lang-bg"
                                className="absolute inset-0 bg-primary rounded-xl -z-10 shadow-lg shadow-primary/25"
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                              />
                            )}
                            {t('spanish')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </SettingSection>
          )}


          {/* Style Technology Section */}
          {userRole === 'pm' && (
            <SettingSection
              title="Tecnología de Estilos"
              desc="Configura el manejador de estilos de tu proyecto. El agente MCP generará componentes compatibles con esta tecnología."
            >
              {isLoading ? <SectionSkeleton /> : (
                <div className="glass-card overflow-hidden border-white/5">
                  <div className="p-8 border-b border-white/5 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-violet-500/10 flex items-center justify-center text-violet-400 border border-violet-500/20 shadow-xl shadow-violet-500/5">
                      <Palette size={24} />
                    </div>
                    <div>
                      <h4 className="text-xl font-display font-bold text-slate-100">Manejador de Estilos</h4>
                      <p className="text-slate-500 text-sm">Selecciona la tecnología que usa tu proyecto para aplicar estilos.</p>
                    </div>
                  </div>
                  <div className="p-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {[
                      { value: 'css',         label: 'CSS Puro',       sub: 'Simple — Sin dependencias',      color: 'blue',   icon: cssIcon },
                      { value: 'sass',        label: 'SASS / SCSS',    sub: 'Escalable Clásico',              color: 'pink',   icon: sassIcon },
                      { value: 'tailwind',    label: 'Tailwind CSS',   sub: 'Moderno — Utility first',        color: 'cyan',   icon: tailwindIcon },
                      { value: 'css-modules', label: 'CSS Modules',    sub: 'Scoped — React nativo',          color: 'emerald',icon: cssModulesIcon },
                      { value: 'css-in-js',   label: 'CSS-in-JS',      sub: 'Dinámico — styled-components',   color: 'amber',  icon: jsIcon },
                      { value: 'bootstrap',   label: 'Bootstrap',      sub: 'Rápido UI — Clásico',            color: 'violet', icon: bootstrapIcon },
                      { value: 'mui',         label: 'Material UI',    sub: 'Rápido UI — Google Design',      color: 'indigo', icon: muiIcon },
                    ].map(({ value, label, sub, color, icon }) => {
                      const isActive = styleHandler === value;
                      const colorMap = {
                        blue:    { ring: 'ring-blue-500/50',    bg: 'bg-blue-500/10',    border: 'border-blue-500/40',    text: 'text-blue-400',    glow: 'shadow-blue-500/20' },
                        pink:    { ring: 'ring-pink-500/50',    bg: 'bg-pink-500/10',    border: 'border-pink-500/40',    text: 'text-pink-400',    glow: 'shadow-pink-500/20' },
                        cyan:    { ring: 'ring-cyan-500/50',    bg: 'bg-cyan-500/10',    border: 'border-cyan-500/40',    text: 'text-cyan-400',    glow: 'shadow-cyan-500/20' },
                        emerald: { ring: 'ring-emerald-500/50', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', text: 'text-emerald-400', glow: 'shadow-emerald-500/20' },
                        amber:   { ring: 'ring-amber-500/50',   bg: 'bg-amber-500/10',   border: 'border-amber-500/40',   text: 'text-amber-400',   glow: 'shadow-amber-500/20' },
                        violet:  { ring: 'ring-violet-500/50',  bg: 'bg-violet-500/10',  border: 'border-violet-500/40',  text: 'text-violet-400',  glow: 'shadow-violet-500/20' },
                        indigo:  { ring: 'ring-indigo-500/50',  bg: 'bg-indigo-500/10',  border: 'border-indigo-500/40',  text: 'text-indigo-400',  glow: 'shadow-indigo-500/20' },
                      };
                      const c = colorMap[color];
                      return (
                        <motion.button
                          key={value}
                          onClick={() => { setStyleHandler(value); setIsDirty(true); }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`relative flex items-center gap-4 p-5 rounded-2xl border transition-all duration-300 text-left ${
                            isActive
                              ? `${c.bg} ${c.border} ring-2 ${c.ring} shadow-xl ${c.glow}`
                              : 'bg-white/[0.02] border-white/5 hover:bg-white/[0.05] hover:border-white/10'
                          }`}
                        >
                          <img
                            src={icon}
                            alt={label}
                            className={`w-7 h-7 object-contain transition-all duration-300 ${
                              isActive ? 'opacity-100' : 'opacity-40 grayscale'
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className={`font-black text-sm ${ isActive ? c.text : 'text-slate-300' }`}>{label}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>
                          </div>
                          {isActive && (
                            <motion.div
                              layoutId="style-handler-check"
                              className={`w-5 h-5 rounded-full ${c.bg} border ${c.border} flex items-center justify-center flex-shrink-0`}
                            >
                              <Check size={11} className={c.text} strokeWidth={3} />
                            </motion.div>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              )}
            </SettingSection>
          )}

          {/* Testing Framework Section */}
          <SettingSection 
            title={t('testingFramework') || 'Testing Framework'} 
            desc={t('testingFrameworkDesc') || 'Choose your preferred testing framework for E2E test generation'}
          >
            {isLoading ? <SectionSkeleton /> : (
              <div className="glass-card overflow-hidden border-white/5 p-1">
                <div className="p-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {[ 
                    { 
                      value: 'playwright', 
                      label: 'Playwright', 
                      sub: 'Modern E2E — Auto-waiting, parallel', 
                      color: 'emerald',
                      icon: '🎭'
                    },
                    { 
                      value: 'cypress', 
                      label: 'Cypress', 
                      sub: 'Developer-friendly — Real-time reloads', 
                      color: 'green',
                      icon: '🌲'
                    },
                    { 
                      value: 'jest', 
                      label: 'Jest', 
                      sub: 'Unit/Integration — Snapshot testing', 
                      color: 'red',
                      icon: '🃏'
                    },
                    { 
                      value: 'vitest', 
                      label: 'Vitest', 
                      sub: 'Fast — Vite-native, modern', 
                      color: 'yellow',
                      icon: '⚡'
                    },
                    { 
                      value: 'testing-library', 
                      label: 'Testing Library', 
                      sub: 'DOM testing — User-centric', 
                      color: 'orange',
                      icon: '🧪'
                    },
                    { 
                      value: 'selenium', 
                      label: 'Selenium', 
                      sub: 'Cross-browser — Classic, robust', 
                      color: 'blue',
                      icon: '🌐'
                    },
                    { 
                      value: 'puppeteer', 
                      label: 'Puppeteer', 
                      sub: 'Chrome DevTools — PDF, screenshots', 
                      color: 'indigo',
                      icon: '🎪'
                    },
                    { 
                      value: 'webdriverio', 
                      label: 'WebdriverIO', 
                      sub: 'Flexible — Protocol agnostic', 
                      color: 'violet',
                      icon: '🤖'
                    }
                  ].map(({ value, label, sub, color, icon }) => {
                    const isActive = testingFramework === value;
                    const colorMap = {
                      emerald: { ring: 'ring-emerald-500/50', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', text: 'text-emerald-400', glow: 'shadow-emerald-500/20' },
                      green:   { ring: 'ring-green-500/50',    bg: 'bg-green-500/10',    border: 'border-green-500/40',    text: 'text-green-400',    glow: 'shadow-green-500/20' },
                      red:     { ring: 'ring-red-500/50',      bg: 'bg-red-500/10',      border: 'border-red-500/40',      text: 'text-red-400',      glow: 'shadow-red-500/20' },
                      yellow:  { ring: 'ring-yellow-500/50',   bg: 'bg-yellow-500/10',   border: 'border-yellow-500/40',   text: 'text-yellow-400',   glow: 'shadow-yellow-500/20' },
                      orange:  { ring: 'ring-orange-500/50',  bg: 'bg-orange-500/10',  border: 'border-orange-500/40',  text: 'text-orange-400',  glow: 'shadow-orange-500/20' },
                      blue:    { ring: 'ring-blue-500/50',     bg: 'bg-blue-500/10',     border: 'border-blue-500/40',     text: 'text-blue-400',     glow: 'shadow-blue-500/20' },
                      indigo:  { ring: 'ring-indigo-500/50',    bg: 'bg-indigo-500/10',    border: 'border-indigo-500/40',    text: 'text-indigo-400',    glow: 'shadow-indigo-500/20' },
                      violet:  { ring: 'ring-violet-500/50',    bg: 'bg-violet-500/10',    border: 'border-violet-500/40',    text: 'text-violet-400',    glow: 'shadow-violet-500/20' },
                    };
                    const c = colorMap[color];
                    return (
                      <motion.button
                        key={value}
                        onClick={() => { setTestingFramework(value); setIsDirty(true); }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`relative flex items-center gap-4 p-5 rounded-2xl border transition-all duration-300 text-left ${
                          isActive
                            ? `bg-white/10 ${c.ring} ring-2 ${c.border} shadow-lg ${c.glow}`
                            : 'bg-white/5 border-white/10 hover:bg-white/[0.07] hover:border-white/20'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl ${c.bg} border ${c.border} flex items-center justify-center text-2xl flex-shrink-0`}>
                          {icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`font-bold text-slate-200 ${isActive ? 'text-white' : ''}`}>
                            {label}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {sub}
                          </div>
                        </div>
                        {isActive && (
                          <motion.div
                            layoutId="testing-framework-check"
                            className={`w-5 h-5 rounded-full ${c.bg} border ${c.border} flex items-center justify-center flex-shrink-0`}
                          >
                            <Check size={11} className={c.text} strokeWidth={3} />
                          </motion.div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}
          </SettingSection>

          {/* Backend Endpoints Section */}
          <SettingSection 
            title={t('backendEndpoints')} 
            desc={t('endpointsDesc')}
          >
            {isLoading ? <SectionSkeleton /> : (
              <div className="glass-card overflow-hidden border-white/5">
                <div className="p-1 border-b border-white/5 bg-white/5 flex flex-col md:flex-row md:items-center justify-between px-6 py-4 gap-4">
                  <div className="flex items-center gap-3">
                    <Globe size={18} className="text-primary" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">{t('backendEndpoints')}</span>
                    <span className="px-2 py-1 rounded bg-slate-900 border border-white/10 text-[10px] font-black text-primary">
                      {totalEndpoints} {t('routes')}
                    </span>
                    {lastSyncedAt && (
                      <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1.5 ml-2">
                        <RotateCw size={10} className="text-slate-600" />
                        {t('lastSynced')}: {new Date(lastSyncedAt).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    )}
                  </div>
                  
                  <div className="relative flex-1 max-w-md">
                    {isFetchingEndpoints && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                      </div>
                    )}
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                    <input 
                      type="text"
                      placeholder={t('search') + '...'}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full bg-slate-950/50 border border-white/10 rounded-xl pl-10 pr-10 py-2 text-sm text-white focus:border-primary focus:outline-none transition-all"
                    />
                  </div>
                </div>
                
                {endpoints.length === 0 && !isFetchingEndpoints ? (
                  <div className="p-12 text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-white/5 mx-auto flex items-center justify-center text-slate-600">
                      <Layers size={32} />
                    </div>
                    <p className="text-slate-500 text-sm max-w-xs mx-auto">
                      {searchQuery ? t('noResults') : t('noEndpoints')}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto custom-scrollbar">
                    {isFetchingEndpoints && endpoints.length === 0 ? (
                      [...Array(5)].map((_, i) => <EndpointItemSkeleton key={i} />)
                    ) : (
                      endpoints.map((endpoint, index) => (
                        <div key={`${endpoint.method}:${endpoint.path}:${index}`} className="p-6 hover:bg-white/[0.02] transition-colors group">
                          <div className="flex flex-col lg:flex-row gap-6 lg:items-center">
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center gap-3">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-tighter ${
                                  endpoint.method === 'GET' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                                  endpoint.method === 'POST' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                                  endpoint.method === 'PATCH' || endpoint.method === 'PUT' ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20' :
                                  'bg-rose-500/10 text-rose-500 border border-rose-500/20'
                                }`}>
                                  {endpoint.method}
                                </span>
                                <code className="text-slate-100 font-mono text-sm font-bold bg-white/5 px-2 py-0.5 rounded">
                                  {endpoint.path}
                                </code>

                                {endpoint.contract && (
                                  <button 
                                    onClick={() => setExpandedEndpoint(expandedEndpoint === `${endpoint.method}:${endpoint.path}:${index}` ? null : `${endpoint.method}:${endpoint.path}:${index}`)}
                                    className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-primary/10 text-primary text-[10px] font-black uppercase tracking-widest hover:bg-primary/20 transition-all ml-auto lg:ml-0"
                                  >
                                    <FileJson size={12} />
                                    {t('payload')}
                                    <motion.div
                                      animate={{ rotate: expandedEndpoint === `${endpoint.method}:${endpoint.path}:${index}` ? 180 : 0 }}
                                      transition={{ duration: 0.2 }}
                                    >
                                      <ChevronDown size={12} />
                                    </motion.div>
                                  </button>
                                )}
                              </div>
                              <input 
                                type="text"
                                value={endpoint.description || ''}
                                onChange={(e) => handleUpdateEndpointDescription(endpoint.method, endpoint.path, e.target.value)}
                                placeholder={t('endpointDescription')}
                                disabled={userRole !== 'pm'}
                                className="w-full bg-transparent border-none p-0 text-sm text-slate-500 focus:text-slate-300 focus:outline-none placeholder:text-slate-700 italic transition-colors"
                              />
                            </div>
                          </div>

                          <AnimatePresence>
                            {expandedEndpoint === `${endpoint.method}:${endpoint.path}:${index}` && endpoint.contract && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden"
                              >
                                <div className="mt-4 p-5 rounded-2xl bg-black/40 border border-white/5 relative group/contract">
                                  <div className="absolute top-3 right-4 text-[10px] font-black uppercase tracking-widest text-slate-700">JSON</div>
                                  <pre className="text-[11px] font-mono text-slate-400 overflow-x-auto custom-scrollbar leading-relaxed">
                                    {endpoint.contract}
                                  </pre>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))
                    )}
                    
                    {hasMoreEndpoints && (
                      <div className="p-4 flex justify-center border-t border-white/5 bg-white/[0.01]">
                        <button 
                          onClick={() => fetchEndpoints(endpointsPage + 1, searchQuery, true)}
                          disabled={isFetchingEndpoints}
                          className="px-6 py-2 rounded-xl bg-white/5 border border-white/10 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:bg-white/10 hover:text-white transition-all disabled:opacity-50"
                        >
                          {isFetchingEndpoints ? t('loading') + '...' : t('loadMore') || 'Load More'}
                        </button>
                      </div>
                    )}

                    {isFetchingEndpoints && endpoints.length > 0 && (
                       <div className="p-10 flex justify-center animate-pulse">
                          <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                       </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </SettingSection>

          {/* Integrations Section */}
          <SettingSection 
            title={t('integrations')} 
            desc={t('integrationDesc')}
          >
            {isLoading ? <SectionSkeleton /> : (
              <div className="space-y-6">
                {/* Figma Integration */}
                <div className="glass-card overflow-hidden border-white/5 group/figma hover:border-fuchsia-500/30 transition-all duration-500 bg-white/[0.01]">
                  <div className="p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
                    <div className="flex items-start gap-6">
                      <div className="relative">
                        <div className="w-14 h-14 rounded-2xl bg-fuchsia-500/10 flex items-center justify-center text-fuchsia-500 border border-fuchsia-500/20 shadow-xl shadow-fuchsia-500/5 relative z-10">
                          <LinkIcon size={28} />
                        </div>
                        <div className="absolute inset-0 bg-fuchsia-500/20 blur-2xl rounded-full opacity-0 group-hover/figma:opacity-50 transition-opacity" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-xl font-display font-bold text-slate-100">{t('figmaConnected')}</h4>
                        <p className="text-slate-500 text-sm leading-relaxed max-w-md">
                          {t('figmaDesc')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                       <button 
                          onClick={() => { if (userRole === 'pm') { setIsFigmaConnected(!isFigmaConnected); setIsDirty(true); } }}
                          disabled={userRole !== 'pm'}
                          className={`relative w-16 h-8 rounded-full transition-all duration-500 border ${isFigmaConnected ? 'bg-fuchsia-500 border-fuchsia-500 shadow-lg shadow-fuchsia-500/20' : 'bg-white/5 border-white/10'} ${userRole !== 'pm' ? 'cursor-not-allowed opacity-50' : ''}`}
                       >
                          <motion.div 
                            className={`absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full shadow-md ${isFigmaConnected ? 'bg-white' : 'bg-slate-500'}`}
                            animate={{ left: isFigmaConnected ? '32px' : '4px' }}
                          />
                       </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Jira Cloud Card */}
                  <form id="jira-config-form" onSubmit={(e) => e.preventDefault()} className="glass-card p-8 border-white/5 bg-white/[0.01] hover:bg-white/[0.02] transition-all group/jira">
                    <div className="flex items-center gap-4 mb-8">
                      <div className="w-12 h-12 rounded-2xl bg-[#0052CC]/10 flex items-center justify-center text-[#0052CC] border border-[#0052CC]/20 shadow-lg shadow-[#0052CC]/5 group-hover/jira:border-[#0052CC]/40 transition-colors">
                        <Globe size={24} />
                      </div>
                      <div>
                        <h4 className="font-black text-white italic">{t('jiraConfig')}</h4>
                        <div className="flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full ${integrationSettings.jira?.enabled ? 'bg-emerald-500 shadow-[0_0_8px_#10b981]' : 'bg-slate-700'}`} />
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                             {integrationSettings.jira?.enabled ? t('active') : t('pending')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="jira-server-url" className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">{t('serverUrl')}</label>
                        <input 
                          id="jira-server-url"
                          name="jira-url"
                          type="text"
                          autoComplete="url"
                          className="w-full bg-black/20 border border-white/5 rounded-xl px-4 py-3 text-sm text-slate-200 focus:border-primary/50 outline-none transition-all"
                          placeholder="https://your-domain.atlassian.net"
                          value={integrationSettings.jira?.url || ''}
                          onChange={(e) => { setIntegrationSettings({ ...integrationSettings, jira: { ...integrationSettings.jira, url: e.target.value } }); setIsDirty(true); }}
                        />
                      </div>
                      <div>
                        <label htmlFor="jira-api-token" className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">{t('apiToken')}</label>
                        <div className="relative">
                          <input 
                            id="jira-api-token"
                            name="jira-token"
                            type="password"
                            autoComplete="current-password"
                            className="w-full bg-black/20 border border-white/5 rounded-xl px-4 py-3 text-sm text-slate-200 focus:border-primary/50 outline-none transition-all pr-10"
                            placeholder="••••••••••••••••"
                            value={integrationSettings.jira?.token || ''}
                            onChange={(e) => { setIntegrationSettings({ ...integrationSettings, jira: { ...integrationSettings.jira, token: e.target.value } }); setIsDirty(true); }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="jira-project-key" className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">{t('projectKey')}</label>
                        <input 
                          id="jira-project-key"
                          name="jira-project"
                          type="text"
                          autoComplete="off"
                          className="w-full bg-black/20 border border-white/5 rounded-xl px-4 py-3 text-sm text-slate-200 focus:border-primary/50 outline-none transition-all font-mono"
                          placeholder="PROJ"
                          value={integrationSettings.jira?.projectKey || ''}
                          onChange={(e) => { setIntegrationSettings({ ...integrationSettings, jira: { ...integrationSettings.jira, projectKey: e.target.value.toUpperCase() } }); setIsDirty(true); }}
                        />
                      </div>
                      <div className="pt-2">
                         <label className={`flex items-center gap-3 w-fit ${(!integrationSettings.jira?.url || !integrationSettings.jira?.token || !integrationSettings.jira?.projectKey) ? 'cursor-not-allowed' : 'cursor-pointer group/toggle'}`}>
                            <button 
                               type="button"
                               onClick={() => { if (integrationSettings.jira?.url && integrationSettings.jira?.token && integrationSettings.jira?.projectKey) { setIntegrationSettings({ ...integrationSettings, jira: { ...integrationSettings.jira, enabled: !integrationSettings.jira?.enabled } }); setIsDirty(true); } }}
                               disabled={!integrationSettings.jira?.url || !integrationSettings.jira?.token || !integrationSettings.jira?.projectKey}
                               className={`relative w-10 h-5 rounded-full transition-all duration-300 border ${integrationSettings.jira?.enabled ? 'bg-emerald-500 border-emerald-500 shadow-lg shadow-emerald-500/20' : 'bg-white/5 border-white/10'} ${(!integrationSettings.jira?.url || !integrationSettings.jira?.token || !integrationSettings.jira?.projectKey) ? 'opacity-30' : ''}`}
                            >
                               <div className={`absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full transition-all ${integrationSettings.jira?.enabled ? 'left-5.5' : 'left-1'}`} />
                            </button>
                            <div className="flex flex-col">
                              <span className={`text-[10px] font-black uppercase tracking-widest transition-colors ${(!integrationSettings.jira?.url || !integrationSettings.jira?.token || !integrationSettings.jira?.projectKey) ? 'text-slate-600' : 'text-slate-500 group-hover/toggle:text-slate-300'}`}>
                                {t('enabled')}
                              </span>
                              {(!integrationSettings.jira?.url || !integrationSettings.jira?.token || !integrationSettings.jira?.projectKey) && (
                                <span className="text-[7px] font-bold text-rose-500/60 uppercase tracking-tighter mt-0.5">{t('requiredFieldsMissing')}</span>
                              )}
                            </div>
                         </label>
                      </div>
                    </div>
                  </form>

                  {/* Azure DevOps Card */}
                  <form id="azure-config-form" onSubmit={(e) => e.preventDefault()} className="glass-card p-8 border-white/5 bg-white/[0.01] hover:bg-white/[0.02] transition-all group/azure">
                    <div className="flex items-center gap-4 mb-8">
                      <div className="w-12 h-12 rounded-2xl bg-[#0078D4]/10 flex items-center justify-center text-[#0078D4] border border-[#0078D4]/20 shadow-lg shadow-[#0078D4]/5 group-hover/azure:border-[#0078D4]/40 transition-colors">
                        <RotateCw size={24} />
                      </div>
                      <div>
                        <h4 className="font-black text-white italic">{t('azureConfig')}</h4>
                        <div className="flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full ${integrationSettings.azure?.enabled ? 'bg-emerald-500 shadow-[0_0_8px_#10b981]' : 'bg-slate-700'}`} />
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                             {integrationSettings.azure?.enabled ? t('active') : t('pending')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="azure-org-url" className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">{t('orgUrl')}</label>
                        <input 
                          id="azure-org-url"
                          name="azure-url"
                          type="text"
                          autoComplete="url"
                          className="w-full bg-black/20 border border-white/5 rounded-xl px-4 py-3 text-sm text-slate-200 focus:border-primary/50 outline-none transition-all"
                          placeholder="https://dev.azure.com/org"
                          value={integrationSettings.azure?.organizationUrl || ''}
                          onChange={(e) => { setIntegrationSettings({ ...integrationSettings, azure: { ...integrationSettings.azure, organizationUrl: e.target.value } }); setIsDirty(true); }}
                        />
                      </div>
                      <div>
                        <label htmlFor="azure-api-token" className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">{t('apiToken')}</label>
                        <input 
                          id="azure-api-token"
                          name="azure-token"
                          type="password"
                          autoComplete="current-password"
                          className="w-full bg-black/20 border border-white/5 rounded-xl px-4 py-3 text-sm text-slate-200 focus:border-primary/50 outline-none transition-all"
                          placeholder="••••••••••••••••"
                          value={integrationSettings.azure?.token || ''}
                          onChange={(e) => { setIntegrationSettings({ ...integrationSettings, azure: { ...integrationSettings.azure, token: e.target.value } }); setIsDirty(true); }}
                        />
                      </div>
                      <div>
                        <label htmlFor="azure-project-key" className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">{t('projectKey')}</label>
                        <input 
                          id="azure-project-key"
                          name="azure-project"
                          type="text"
                          autoComplete="off"
                          className="w-full bg-black/20 border border-white/5 rounded-xl px-4 py-3 text-sm text-slate-200 focus:border-primary/50 outline-none transition-all"
                          placeholder="Project Name"
                          value={integrationSettings.azure?.projectKey || ''}
                          onChange={(e) => { setIntegrationSettings({ ...integrationSettings, azure: { ...integrationSettings.azure, projectKey: e.target.value } }); setIsDirty(true); }}
                        />
                      </div>
                      <div className="pt-2">
                         <label className={`flex items-center gap-3 w-fit ${(!integrationSettings.azure?.organizationUrl || !integrationSettings.azure?.token || !integrationSettings.azure?.projectKey) ? 'cursor-not-allowed' : 'cursor-pointer group/toggle'}`}>
                            <button 
                               type="button"
                               onClick={() => { if (integrationSettings.azure?.organizationUrl && integrationSettings.azure?.token && integrationSettings.azure?.projectKey) { setIntegrationSettings({ ...integrationSettings, azure: { ...integrationSettings.azure, enabled: !integrationSettings.azure?.enabled } }); setIsDirty(true); } }}
                               disabled={!integrationSettings.azure?.organizationUrl || !integrationSettings.azure?.token || !integrationSettings.azure?.projectKey}
                               className={`relative w-10 h-5 rounded-full transition-all duration-300 border ${integrationSettings.azure?.enabled ? 'bg-emerald-500 border-emerald-500 shadow-lg shadow-emerald-500/20' : 'bg-white/5 border-white/10'} ${(!integrationSettings.azure?.organizationUrl || !integrationSettings.azure?.token || !integrationSettings.azure?.projectKey) ? 'opacity-30' : ''}`}
                            >
                               <div className={`absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full transition-all ${integrationSettings.azure?.enabled ? 'left-5.5' : 'left-1'}`} />
                            </button>
                            <div className="flex flex-col">
                              <span className={`text-[10px] font-black uppercase tracking-widest transition-colors ${(!integrationSettings.azure?.organizationUrl || !integrationSettings.azure?.token || !integrationSettings.azure?.projectKey) ? 'text-slate-600' : 'text-slate-500 group-hover/toggle:text-slate-300'}`}>
                                {t('enabled')}
                              </span>
                              {(!integrationSettings.azure?.organizationUrl || !integrationSettings.azure?.token || !integrationSettings.azure?.projectKey) && (
                                <span className="text-[7px] font-bold text-rose-500/60 uppercase tracking-tighter mt-0.5">{t('requiredFieldsMissing')}</span>
                              )}
                            </div>
                         </label>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </SettingSection>

          {/* Desktop Save Button - MOVED TO SIDEBAR */}

          {/* Global Save Button for mobile/small screens */}
          {userRole === 'pm' && (
            <div className="lg:hidden pt-8">
              <button 
                onClick={handleSaveSettings}
                disabled={isSaving || isLoading}
                className={`w-full h-14 flex items-center justify-center gap-3 rounded-2xl text-sm font-black uppercase tracking-widest transition-all shadow-2xl active:scale-95 ${
                  saveSuccess 
                    ? 'bg-emerald-500 text-white shadow-emerald-500/20' 
                    : 'bg-primary text-white shadow-primary/20'
                }`}
              >
                {isSaving ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : saveSuccess ? (
                  <Check size={20} strokeWidth={3} />
                ) : (
                  <Save size={20} strokeWidth={2.5} />
                )}
                {saveSuccess ? t('confirmed') : t('saveSettings')}
              </button>
            </div>
          )}

        </div>

        {/* Documentation Sidebar */}
        <aside className="lg:col-span-4 space-y-6">

           <div className="glass-card p-8 bg-gradient-to-br from-primary/10 to-transparent border-primary/20 relative overflow-hidden group">
              <div className="absolute -right-8 -bottom-8 text-primary opacity-5 group-hover:scale-110 transition-transform duration-700">
                 <ShieldCheck size={160} />
              </div>
              <h3 className="text-xl font-bold text-slate-200 mb-4">{t('documentation')}</h3>
              <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                {t('documentationDesc')}
              </p>
              <ul className="space-y-3">
                 <li><button onClick={() => openDoc('sdk')} className="text-sm font-semibold text-primary hover:text-cyan-400 flex items-center gap-2 group/inline"><ExternalLink size={14} /> {t('sdkReference')} <ArrowUpRight size={14} className="opacity-0 group-hover/inline:opacity-100 transition-opacity" /></button></li>
                 <li><button onClick={() => openDoc('mcp')} className="text-sm font-semibold text-primary hover:text-cyan-400 flex items-center gap-2 group/inline"><ExternalLink size={14} /> {t('mcpConfiguration')} <ArrowUpRight size={14} className="opacity-0 group-hover/inline:opacity-100 transition-opacity" /></button></li>
                 <li><button onClick={() => openDoc('security')} className="text-sm font-semibold text-primary hover:text-cyan-400 flex items-center gap-2 group/inline"><ExternalLink size={14} /> {t('securityWhitepaper')} <ArrowUpRight size={14} className="opacity-0 group-hover/inline:opacity-100 transition-opacity" /></button></li>
              </ul>
              
              <div className="mt-8 pt-6 border-t border-white/10">
                 <div className="flex items-center gap-3 p-3 rounded-xl bg-background/50 border border-white/5">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_#10b981]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('systemStatusOperational')}</span>
                 </div>
              </div>
           </div>

           {/* Sticky Save Button - Desktop */}
           {userRole === 'pm' && (
             <div className="hidden lg:block sticky top-24 z-20 w-full mt-6">
               <button 
                  onClick={handleSaveSettings}
                  disabled={isSaving || isLoading}
                  className={`w-full h-14 flex items-center justify-center gap-3 px-12 rounded-2xl text-sm font-black uppercase tracking-widest transition-all shadow-2xl active:scale-95 border border-white/5 ${
                    saveSuccess 
                      ? 'bg-emerald-500 text-white shadow-emerald-500/20 border-emerald-400/20' 
                      : 'bg-primary text-white hover:bg-blue-600 shadow-primary/20 hover:shadow-primary/40 border-white/10'
                  }`}
                >
                  {isSaving ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : saveSuccess ? (
                    <Check size={20} strokeWidth={3} />
                  ) : (
                    <Save size={20} strokeWidth={2.5} />
                  )}
                  {saveSuccess ? t('confirmed') : t('saveSettings')}
                </button>
             </div>
           )}
        </aside>
      </div>

      {createPortal(
        <AnimatePresence mode='wait'>
          {isDocModalOpen && (
            <DocModal 
              key="settings-doc-modal"
              type={activeDocType}
              onClose={() => setIsDocModalOpen(false)}
              t={t}
              onContactSupport={() => setIsContactModalOpen(true)}
            />
          )}
        </AnimatePresence>,
        document.body
      )}

      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        t={t}
        initialEmail={user?.email}
        source="doc_modal_settings"
      />
    </div>
  );
};

export default Settings;
